/**
 * Who is meeting whom (2026-10-09, item 7, flag `team_schedule`, default off).
 *
 * Every interview and orientation session can be assigned to up to MAX_ASSIGNEES team members of
 * the booking's organization (an interviewer, a manager). Assigned team members get the same
 * calendar invite as the host (same UID and SEQUENCE, themselves as the ATTENDEE), the update and
 * cancel invites, the reminders, and see the session on their own calendar. The Team schedule tab
 * shows every interview and orientation of the org: who, which candidate(s), when, where, kind.
 *
 * Pure (no Firebase imports): copied verbatim into functions/src/generated/ by
 * scripts/syncFunctionsGenerated.mjs, so the server validates assignees and builds the schedule with
 * exactly the code the offline check (scripts/qa/offline-team-schedule.mjs) proves.
 */
import { STAGE_LABELS, type InterviewStage, type LocationType } from './booking';
import { MS_PER_DAY, addDaysToDate, formatDateInZone, zonedDateKey, zonedParts, zonedWallTimeToUtcMs } from './time';
import { resolveRecipientTimeZone } from '../notifications/quietHours';

export const TEAM_SCHEDULE_FLAG = 'team_schedule';
export const TEAM_SCHEDULE_FLAG_LABEL = 'Team schedule and session assignments';
export const TEAM_SCHEDULE_FLAG_DESCRIPTION =
  'Interviews and orientation sessions can be assigned to team members (interviewer, manager). Assigned team members get the calendar invite, updates and reminders, and the Scheduling page gets a Team schedule tab showing every interview and orientation of the organization. Off: only the person who booked a session gets its invite.';

/** Most team members on one interview or session (besides the host). */
export const MAX_ASSIGNEES = 5;
/** Longest range the Team schedule loads at once. */
export const TEAM_SCHEDULE_MAX_RANGE_MS = 31 * MS_PER_DAY;

export type TeamMemberRef = { uid: string; name: string };

/** What the server knows about a would-be assignee: their profile's role and organization. */
export type AssigneeCandidate = { uid: string; role?: string | null; orgId?: string | null; name?: string | null };

/**
 * Validates the team members picked for a booking. Every one must be an employer account of the
 * booking's own organization. The host is dropped (they already get everything), duplicates are
 * dropped, and at most MAX_ASSIGNEES remain. `known` is what the server read for each requested uid
 * (a uid with no profile is simply absent).
 */
export function validateAssignees(
  requested: unknown,
  known: AssigneeCandidate[],
  opts: { orgId: string; hostUid: string; max?: number },
): { uids: string[]; members: TeamMemberRef[]; error: string | null; refused: string[] } {
  const max = opts.max ?? MAX_ASSIGNEES;
  if (requested == null) return { uids: [], members: [], error: null, refused: [] };
  if (!Array.isArray(requested)) return { uids: [], members: [], error: 'Pick team members from your team list.', refused: [] };
  const wanted: string[] = [];
  for (const r of requested) {
    if (typeof r !== 'string') return { uids: [], members: [], error: 'Pick team members from your team list.', refused: [] };
    const u = r.trim();
    if (!u || u.length > 128 || u.includes('/')) return { uids: [], members: [], error: 'Pick team members from your team list.', refused: [] };
    if (u === opts.hostUid || wanted.includes(u)) continue;
    wanted.push(u);
  }
  if (wanted.length > max) return { uids: [], members: [], error: `Assign at most ${max} team members to one session.`, refused: [] };
  const byUid = new Map(known.map((k) => [k.uid, k]));
  const refused = wanted.filter((u) => {
    const k = byUid.get(u);
    return !k || k.role !== 'employer' || !opts.orgId || k.orgId !== opts.orgId;
  });
  if (refused.length) {
    return { uids: [], members: [], error: 'Only people on your organization\'s team can be assigned. Someone you picked is not on your team.', refused };
  }
  const members = wanted.map((u) => ({ uid: u, name: (byUid.get(u)?.name || '').trim() || 'Team member' }));
  return { uids: wanted, members, error: null, refused: [] };
}

/** Who was added and removed when a booking's assignees change (drives the invite / cancel emails). */
export function assignmentDiff(before: string[], after: string[]): { added: string[]; removed: string[]; kept: string[] } {
  const b = new Set(before);
  const a = new Set(after);
  return {
    added: after.filter((u) => !b.has(u)),
    removed: before.filter((u) => !a.has(u)),
    kept: after.filter((u) => b.has(u)),
  };
}

/** Reminder recipient key for an assigned team member (a held reminder email's `recipient`). */
export const ASSIGNEE_RECIPIENT_PREFIX = 'assignee:';
export const assigneeRecipient = (uid: string) => `${ASSIGNEE_RECIPIENT_PREFIX}${uid}`;
export const assigneeFromRecipient = (recipient: string | null | undefined): string | null =>
  recipient && recipient.startsWith(ASSIGNEE_RECIPIENT_PREFIX) ? recipient.slice(ASSIGNEE_RECIPIENT_PREFIX.length) || null : null;

/* ------------------------------------------------------------------ schedule */

export type TeamScheduleKind = 'interview' | 'onboarding';

export type TeamScheduleEntry = {
  id: string;
  kind: TeamScheduleKind;
  title: string;
  status: 'booked' | 'cancelled';
  startMs: number;
  endMs: number;
  hostUid: string;
  hostName: string;
  hostTimeZone: string;
  assignees: TeamMemberRef[];
  candidates: TeamMemberRef[];
  jobTitle: string | null;
  jobId: string | null;
  applicationId: string | null;
  locationType: LocationType;
  location: string;
};

const LOCATION_TYPES: LocationType[] = ['in_person', 'video', 'phone'];

function s(v: unknown, max = 200): string {
  return typeof v === 'string' ? v.trim().slice(0, max) : '';
}

function people(v: unknown): TeamMemberRef[] {
  return (Array.isArray(v) ? v : [])
    .map((p) => ({ uid: s((p as { uid?: unknown })?.uid, 128), name: s((p as { name?: unknown })?.name, 120) }))
    .filter((p) => p.uid);
}

/** One stored schedulingBookings doc as a Team schedule row (null when it is not a usable booking). */
export function buildTeamScheduleEntry(id: string, b: Record<string, unknown> | null | undefined): TeamScheduleEntry | null {
  if (!b) return null;
  const startMs = Number(b.startMs);
  const endMs = Number(b.endMs);
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) return null;
  const kind: TeamScheduleKind = b.kind === 'onboarding' ? 'onboarding' : 'interview';
  const title = kind === 'onboarding' ? s(b.sessionTitle, 120) || 'Orientation session' : STAGE_LABELS[b.stage as InterviewStage] || 'Interview';
  return {
    id,
    kind,
    title,
    status: b.status === 'cancelled' ? 'cancelled' : 'booked',
    startMs,
    endMs,
    hostUid: s(b.hostUid, 128),
    hostName: s(b.hostName, 120) || 'Recruiter',
    // The shared zone chain (quietHours.resolveRecipientTimeZone): a stored host zone, else Mountain.
    hostTimeZone: resolveRecipientTimeZone({ storedTimeZone: s(b.hostTimeZone, 64) }),
    assignees: people(b.assignees),
    candidates: people(b.attendees),
    jobTitle: s(b.jobTitle, 200) || null,
    jobId: s(b.jobId, 128) || null,
    applicationId: s(b.applicationId, 200) || null,
    locationType: LOCATION_TYPES.includes(b.locationType as LocationType) ? (b.locationType as LocationType) : 'in_person',
    location: s(b.location, 300),
  };
}

/** Everyone from the org who is on an entry (host first, then assignees). */
export function entryTeam(e: TeamScheduleEntry): TeamMemberRef[] {
  const out: TeamMemberRef[] = [];
  if (e.hostUid) out.push({ uid: e.hostUid, name: e.hostName });
  for (const a of e.assignees) if (!out.some((o) => o.uid === a.uid)) out.push(a);
  return out;
}

export type TeamScheduleFilter = { memberUid?: string | null; kind?: TeamScheduleKind | 'all' | null; includeCancelled?: boolean };

/** Entries in [fromMs, toMs) that match the filters, earliest first. */
export function filterTeamSchedule(entries: TeamScheduleEntry[], range: { fromMs: number; toMs: number }, f: TeamScheduleFilter = {}): TeamScheduleEntry[] {
  return entries
    .filter((e) => e.endMs > range.fromMs && e.startMs < range.toMs)
    .filter((e) => f.includeCancelled || e.status === 'booked')
    .filter((e) => !f.kind || f.kind === 'all' || e.kind === f.kind)
    .filter((e) => !f.memberUid || entryTeam(e).some((m) => m.uid === f.memberUid))
    .sort((a, b) => a.startMs - b.startMs || a.id.localeCompare(b.id));
}

/** The team members who appear anywhere in the loaded schedule (the "Team member" filter). */
export function scheduleMembers(entries: TeamScheduleEntry[]): TeamMemberRef[] {
  const map = new Map<string, string>();
  for (const e of entries) for (const m of entryTeam(e)) if (!map.has(m.uid) || map.get(m.uid) === 'Team member') map.set(m.uid, m.name);
  return [...map.entries()].map(([uid, name]) => ({ uid, name })).sort((a, b) => a.name.localeCompare(b.name));
}

export type TeamScheduleView = 'day' | 'week';

/**
 * The range a day or week view covers, in the VIEWER's zone: a day is local midnight to the next
 * local midnight; a week starts on Monday. Correct across daylight-saving changes.
 */
export function teamScheduleRange(anchorMs: number, view: TeamScheduleView, tz: string): { fromMs: number; toMs: number; days: string[] } {
  const p = zonedParts(anchorMs, tz);
  const back = view === 'week' ? (p.weekday + 6) % 7 : 0;
  const start = addDaysToDate(p.year, p.month, p.day, -back);
  const count = view === 'week' ? 7 : 1;
  const end = addDaysToDate(start.year, start.month, start.day, count);
  const days: string[] = [];
  for (let i = 0; i < count; i++) {
    const d = addDaysToDate(start.year, start.month, start.day, i);
    days.push(`${d.year}-${String(d.month).padStart(2, '0')}-${String(d.day).padStart(2, '0')}`);
  }
  return {
    fromMs: zonedWallTimeToUtcMs(start.year, start.month, start.day, 0, 0, tz),
    toMs: zonedWallTimeToUtcMs(end.year, end.month, end.day, 0, 0, tz),
    days,
  };
}

/** Moves the anchor one day or one week (by calendar days in the viewer's zone, so DST never skips a day). */
export function shiftAnchor(anchorMs: number, view: TeamScheduleView, dir: -1 | 1, tz: string): number {
  const p = zonedParts(anchorMs, tz);
  const d = addDaysToDate(p.year, p.month, p.day, dir * (view === 'week' ? 7 : 1));
  return zonedWallTimeToUtcMs(d.year, d.month, d.day, 12, 0, tz);
}

/** Groups entries by the viewer's local calendar day; every day of the range is present (empty days too). */
export function groupByDay(entries: TeamScheduleEntry[], days: string[], tz: string): Array<{ key: string; label: string; entries: TeamScheduleEntry[] }> {
  return days.map((key) => {
    const [y, m, d] = key.split('-').map(Number);
    const noon = zonedWallTimeToUtcMs(y, m, d, 12, 0, tz);
    return { key, label: formatDateInZone(noon, tz), entries: entries.filter((e) => zonedDateKey(e.startMs, tz) === key) };
  });
}
